import React from 'react';
import Icon from '../../../components/AppIcon';

const ProfileCard = ({ profile, isSelected, onSelect, className = '' }) => {
  const handleCardClick = () => {
    onSelect(profile?.id);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      onSelect(profile?.id);
    }
  };

  return (
    <div
      className={`
        relative bg-surface border-2 rounded-xl p-6 cursor-pointer transition-smooth hover-scale
        min-h-[650px] flex flex-col
        ${isSelected 
          ? 'border-primary bg-primary/5 shadow-modal' 
          : 'border-border hover:border-primary/50 hover:shadow-subtle'
        }
        ${className}
      `}
      onClick={handleCardClick}
      onKeyPress={handleKeyPress}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={`Select ${profile?.title} program`}
    >
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
          <Icon name="Check" size={16} color="white" strokeWidth={2} />
        </div>
      )}
      {/* Profile Icon */}
      <div className={`
        w-16 h-16 rounded-lg flex items-center justify-center mb-4
        ${isSelected ? 'bg-primary text-white' : 'bg-muted text-primary'}
      `}>
        <Icon name={profile?.icon} size={32} strokeWidth={2} />
      </div>
      {/* Profile Title - Fixed Height */}
      <h3 className="text-xl font-semibold text-foreground mb-2 min-h-[28px]">
        {profile?.title}
      </h3>
      {/* Profile Description - Fixed Height */}
      <p className="text-text-secondary text-sm mb-4 leading-relaxed min-h-[60px]">
        {profile?.description}
      </p>
      {/* Program Details - Fixed Height */}
      <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={14} color="var(--color-text-secondary)" strokeWidth={2} />
            <span className="text-sm font-medium text-foreground">Duration</span>
          </div>
          <span className="text-sm font-semibold text-text-secondary bg-background px-2 py-1 rounded">
            {profile?.duration}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="IndianRupee" size={14} color="var(--color-text-secondary)" strokeWidth={2} />
            <span className="text-sm font-medium text-foreground">Registration Fee</span>
          </div>
          <span className="text-lg font-bold text-primary bg-primary/10 px-2 py-1 rounded">
            ₹{profile?.fee?.toLocaleString('en-IN')}
          </span>
        </div>
      </div>
      {/* Key Benefits - Standardized to 5 items with fixed height */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-foreground mb-3">Key Benefits:</h4>
        <div className="space-y-2 min-h-[130px]">
          {profile?.benefits?.slice(0, 5)?.map((benefit, index) => (
            <div key={index} className="flex items-start space-x-2">
              <Icon 
                name="CheckCircle" 
                size={16} 
                color="var(--color-success)" 
                strokeWidth={2}
                className="mt-0.5 flex-shrink-0"
              />
              <span className="text-sm text-text-secondary leading-snug">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Desktop-only Curriculum Highlights - Fixed Height */}
      <div className="hidden lg:block mt-auto pt-4 border-t border-border">
        <h4 className="text-sm font-medium text-foreground mb-3">Curriculum Highlights:</h4>
        <div className="grid grid-cols-2 gap-2 min-h-[120px]">
          {profile?.curriculum?.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0"></div>
              <span className="text-xs text-text-secondary leading-tight">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
